// BUILD-046 correction cycle 1bb88b3e (stage B1, item N1): ONE toolbar for Mine, Shared and Team.
//
// The Owner's review: the controls changed from tab to tab (Assign task only on some, density and sort only on Mine, no search). The same toolbar model now
// serves the three views, so a control is never present in one view and missing in another for no reason:
//   Assign task : shown when the Caller may assign (`GET /work/me` -> canAssign === true; absent means false). It does NOT depend on the Team view
//                 (`canOversee`) or on group administration: any active Employee can assign, and an Employee with no Direct_Reports still sees it.
//   Density     : Comfortable / Compact, one preference for all three views and across tab changes (densityStore.ts).
//   Sort        : the sort modes that apply to the view (pageSortModel.ts: Recommended, the column orders - Task, Assigned to, Priority, Task due date, Status - and
//                 Oldest / Newest / Project). A saved mode the view does not offer falls back to Recommended. Nothing here implements sorting.
//   Search      : a small search scoped to the CURRENT view (viewSearchModel.ts); the toolbar only describes the box, the view filters its loaded items and
//                 says when the list is not fully loaded.
//
// Pure module. Type-only imports plus the sort and density models. Erasable TypeScript only.

import { DENSITY_OPTIONS, isDensity } from './densityStore';
import type { Density } from './densityStore';
import type { HostView } from './hostViewsModel';
import { PAGE_SORT_LABELS, applicablePageSortModes, effectivePageSortMode } from './pageSortModel';
import type { PageSortMode } from './pageSortModel';
import { requestedColumnShown } from './showRequesterStore';

/** The toolbar's view: one of the host's three views, or Assigned by Me (increment 1a: it has its own Sort modes, issuedOrdering.ts). */
export type ToolbarViewName = HostView | 'issued';

export const ASSIGN_TASK_LABEL = 'Assign task';

export interface ToolbarMe {
  /** From `GET /work/me`. Absent (an older server) is read as false. */
  canAssign?: boolean;
}

export interface ToolbarInput {
  view: ToolbarViewName;
  me: ToolbarMe | null;
  density: unknown;
  /** The sort mode the Caller chose (any value; one the view does not offer falls back to Recommended). */
  sort: unknown;
  /** The query typed in THIS view. */
  search: string;
  /** The tasks the view has loaded (only `projectDisplayName` is read, to decide whether Project is offered). */
  loaded?: readonly { projectDisplayName?: unknown }[];
}

export interface ToolbarView {
  view: ToolbarViewName;
  assignTask: { visible: boolean; label: typeof ASSIGN_TASK_LABEL };
  density: { value: Density; options: { value: Density; label: string; selected: boolean }[] };
  sort: { value: PageSortMode; options: { value: PageSortMode; label: string; selected: boolean }[] };
  search: { value: string; label: string; placeholder: string };
}

const VIEW_NAME: Readonly<Record<ToolbarViewName, string>> = Object.freeze({ 'my-work': 'My Work', 'group-shared': 'the shared queue', 'team-work': 'Team Work', issued: 'Assigned by Me' });

/** Whether the Caller is offered Assign task. The server decides every assignment; this only avoids offering what it will refuse. */
export function assignTaskVisible(me: ToolbarMe | null | undefined): boolean {
  return me?.canAssign === true;
}

export function describeToolbar(input: ToolbarInput): ToolbarView {
  const density: Density = isDensity(input.density) ? input.density : 'comfortable';
  const loaded = input.loaded ?? [];
  const effective = effectivePageSortMode(input.sort, input.view, loaded);
  const name = VIEW_NAME[input.view];
  return {
    view: input.view,
    assignTask: { visible: assignTaskVisible(input.me), label: ASSIGN_TASK_LABEL },
    density: { value: density, options: DENSITY_OPTIONS.map((o) => ({ ...o, selected: o.value === density })) },
    sort: { value: effective, options: applicablePageSortModes(input.view, loaded).map((mode) => ({ value: mode, label: PAGE_SORT_LABELS[mode], selected: mode === effective })) },
    search: { value: typeof input.search === 'string' ? input.search : '', label: `Search ${name}`, placeholder: 'Search by title or person' },
  };
}

// ═════════════════════════ increment 1b-ii: the one toolbar row, the density buttons and the Display section ═════════════════════════
//
// ONE row, left to right: Search | Status | Filter | Sort | (a reserved Presets slot). The selected-filter chips follow in the same wrapping row, so on a narrow screen the row wraps instead of
// pushing the page wider. There is NO "View" menu any more: the Comfortable / Compact choice is two icon-only buttons at the right end of the tab row (DensityButtons.tsx), and "Show requester" is in the Filter
// popover under a "Display" section. A control that does not apply to a tab is still not drawn (Status needs status choices, Filter needs filters, Sort needs sort modes).

/** The controls of the one toolbar row, in order. `presets` is a reserved, EMPTY slot (saved views arrive in a later increment): it draws nothing, so nothing moves when they do. */
export const TOOLBAR_CONTROL_ORDER: readonly ['search', 'status', 'filter', 'sort', 'presets'] = Object.freeze(['search', 'status', 'filter', 'sort', 'presets'] as const);

export type ToolbarControlKey = (typeof TOOLBAR_CONTROL_ORDER)[number];

/** The Presets slot: reserved space only. No visible control, no text, not focusable, hidden from assistive technology. */
export const PRESETS_SLOT = Object.freeze({ key: 'presets' as const, reserved: true as const, visible: false as const, focusable: false as const, text: null });

/** The search box: about 256 px wide (Tailwind w-64) on every tab, and NO character limit (the input has no `maxLength`). */
export const SEARCH_BOX = Object.freeze({ widthPx: 256, widthClass: 'w-64', maxLength: null });

/** What a tab offers the toolbar. `filter` is a tab that has filters; `display` is the Display section (describeDisplaySection) being on offer. */
export interface ToolbarApplies {
  status: boolean;
  filter: boolean;
  display: boolean;
  sort: boolean;
}

/**
 * The controls the one toolbar row DRAWS, in the one fixed order: Search | Status | Filter | Sort | Presets slot. Search and the Presets slot are always in the row. Status needs status choices and
 * Sort needs sort modes. Filter is drawn when the tab has a filter OR the Display section is on offer (so "Show requester" stays reachable where there is no other filter); a Filter popover with
 * nothing in it is never drawn.
 */
export function describeToolbarRow(applies: ToolbarApplies): ToolbarControlKey[] {
  return TOOLBAR_CONTROL_ORDER.filter((key) => {
    if (key === 'status') return applies.status === true;
    if (key === 'filter') return applies.filter === true || applies.display === true;
    if (key === 'sort') return applies.sort === true;
    return true;
  });
}

export const FILTER_PANEL_LABEL_TASKS = 'Filter tasks';
export const FILTER_PANEL_LABEL_REQUESTS = 'Filter requests';

/** The Filter popover's accessible name: what it filters. Assigned by Me lists requests, every other tab lists tasks. */
export function filterPanelLabel(view: ToolbarViewName): string {
  return view === 'issued' ? FILTER_PANEL_LABEL_REQUESTS : FILTER_PANEL_LABEL_TASKS;
}

export interface DensityButtonView {
  value: Density;
  /** The short word ("Comfortable"); never drawn, because the buttons are icon-only. */
  label: string;
  /** The accessible name and the tooltip: "Comfortable view" / "Compact view". */
  ariaLabel: string;
  title: string;
  /** `aria-pressed`: the selected density. */
  pressed: boolean;
}

export const DENSITY_BUTTON_NAME: Readonly<Record<Density, string>> = Object.freeze({ comfortable: 'Comfortable view', compact: 'Compact view' });

/** The two density buttons, Comfortable then Compact; the one that is set is pressed (anything that is not a density reads as Comfortable). */
export function describeDensityButtons(density: unknown): DensityButtonView[] {
  const current: Density = isDensity(density) ? density : 'comfortable';
  return DENSITY_OPTIONS.map((option) => ({ value: option.value, label: option.label, ariaLabel: DENSITY_BUTTON_NAME[option.value], title: DENSITY_BUTTON_NAME[option.value], pressed: option.value === current }));
}

export const DISPLAY_SECTION_TITLE = 'Display';
export const SHOW_REQUESTER_LABEL = 'Show requester';

export interface DisplaySectionView {
  title: typeof DISPLAY_SECTION_TITLE;
  items: { key: 'show-requester'; label: typeof SHOW_REQUESTER_LABEL; checked: boolean }[];
}

/**
 * The "Display" section of the Filter popover. It holds "Show requester" (an on / off setting, OFF by default, kept by showRequesterStore exactly as before) and is offered where it did
 * its work when it was in the View menu: only while Compact is selected, and never on Assigned by Me (which has no requester to show). Otherwise null: the section is not drawn.
 */
export function describeDisplaySection(input: { view: ToolbarViewName; density: unknown; showRequester: boolean }): DisplaySectionView | null {
  if (!requestedColumnShown(input.density, true, input.view === 'issued' ? 'issued' : 'mine')) return null;
  return { title: DISPLAY_SECTION_TITLE, items: [{ key: 'show-requester', label: SHOW_REQUESTER_LABEL, checked: input.showRequester === true }] };
}
